import {
  additionalWorkBillableTotal,
  additionalWorkPrice,
  isBillableAdditionalWorkStatus,
} from './generalAdditionalWork'
import {
  extraServiceLineBillableTotal,
  extraServiceLineJobScopeSummary,
  extraServiceLinePrice,
  isBillableExtraServiceLineStatus,
} from './generalExtraServiceLines'
const { invoiceFromVatDocument, vatDocumentHtml } = require('./vatDocument.js')

export const JOB_FINANCIAL_SELECT = `*, job_price_adjustments(*), job_additional_work_requests(id,status,price_amount,labour_amount,parts_amount), job_extra_service_line_requests(id,module_key,status,line_amount,service_id,service_snapshot), job_stock_usage(id,status,charged_to_customer,quantity,unit_type,stock_item_id,stock_name_snapshot,stock_internal_code_snapshot,stock_supplier_part_number_snapshot,stock_supplier_snapshot,unit_cost_snapshot,unit_sell_price_snapshot,customer_description,source_type,additional_work_request_id,confirmed_at${process.env.REACT_APP_VAT_BUILD3 === 'true' ? ',vat_price_snapshot' : ''})`

// The workshop's own calendar date. toISOString() gives the UTC date, which is
// still yesterday between midnight and 1am during British Summer Time.
export function localDateString(date = new Date()) {
  const pad = value => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

// Jobs booked in for a day, by the same rule the calendar uses.
export function jobsBookedOn(jobs, dateStr) {
  return jobs.filter(job => job.scheduled_date === dateStr && !['cancelled', 'voided'].includes(normaliseJobStatus(job.status)))
}

export function fmtDate(d) {
  if (!d) return '—'
  return new Date(d + 'T12:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

export function fmtDuration(mins) {
  if (!mins) return '—'
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return h > 0 ? `${h}h ${m}m` : `${m}m`
}

export function calcMins(start, finish) {
  if (!start || !finish) return 0
  const [sh, sm] = start.split(':').map(Number)
  const [fh, fm] = finish.split(':').map(Number)
  const mins = (fh * 60 + fm) - (sh * 60 + sm)
  return mins > 0 ? mins : 0
}

export function jobModuleKey(job = {}) {
  const key = String(job?.module_key || 'suspension').trim().toLowerCase()
  return ['suspension', 'general'].includes(key) ? key : 'suspension'
}

export function isSuspensionJob(job = {}) {
  return jobModuleKey(job) === 'suspension'
}

export function isGeneralJob(job = {}) {
  return jobModuleKey(job) === 'general'
}

export function jobModuleLabel(job = {}) {
  return isGeneralJob(job) ? 'General Workshop' : 'Suspension'
}

export function jobPricingMode(job = {}) {
  const mode = String(job?.pricing_mode || 'fixed').trim().toLowerCase()
  return ['fixed', 'from', 'quote_required'].includes(mode) ? mode : 'fixed'
}

export function jobPriceStatus(job = {}) {
  const status = String(job?.price_status || 'final').trim().toLowerCase()
  return ['final', 'estimate', 'quote_required'].includes(status) ? status : 'final'
}

export function generalJobDormantMessage() {
  return 'Use the General job card to track requested work, pricing, technician, and handover notes.'
}

export function moneyNumber(value) {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

export function serviceSummaryTotal(summary) {
  const matches = String(summary || '').match(/(?:GBP|£)\s*([0-9]+(?:\.[0-9]{1,2})?)/gi) || []
  return matches.reduce((sum, match) => {
    const amount = Number(match.replace(/(?:GBP|£)/i, '').trim())
    return Number.isFinite(amount) ? sum + amount : sum
  }, 0)
}

export function normaliseJobPartsUsed(value) {
  let rows = value
  if (!rows) return []
  if (!Array.isArray(rows)) {
    try {
      rows = JSON.parse(rows)
    } catch {
      rows = []
    }
  }

  return rows
    .filter(row => row && typeof row === 'object')
    .map((row, index) => {
      const quantity = moneyNumber(row.quantity || row.qty || 1) || 1
      const unitCost = moneyNumber(row.unit_cost ?? row.cost_price ?? row.cost)
      const unitPrice = moneyNumber(row.unit_price ?? row.sell_price ?? row.price)
      return {
        id: row.id || `part-${index}`,
        name: String(row.name || row.description || '').trim(),
        part_number: String(row.part_number || row.partNumber || row.code || '').trim(),
        supplier: String(row.supplier || '').trim(),
        quantity,
        unit_cost: unitCost,
        unit_price: unitPrice,
        source_type: String(row.source_type || row.source || '').trim(),
        stock_item_id: String(row.stock_item_id || '').trim(),
        stock_usage_id: String(row.stock_usage_id || row.job_stock_usage_id || '').trim(),
      }
    })
    .filter(row => row.name || row.part_number || row.supplier || row.unit_cost || row.unit_price)
}

export function jobPartsTotals(value) {
  const partsUsed = normaliseJobPartsUsed(value)
  const costTotal = partsUsed.reduce((sum, row) => sum + (row.quantity * row.unit_cost), 0)
  const sellTotal = partsUsed.reduce((sum, row) => sum + (row.quantity * row.unit_price), 0)
  return { partsUsed, costTotal, sellTotal }
}

function booleanValue(value) {
  if (value === true) return true
  if (value === false || value === null || value === undefined) return false
  return ['true', '1', 'yes', 'y'].includes(String(value).trim().toLowerCase())
}

function normaliseJobStockUsage(value) {
  let rows = value
  if (!rows) return []
  if (!Array.isArray(rows)) {
    try {
      rows = JSON.parse(rows)
    } catch {
      rows = []
    }
  }

  return rows
    .filter(row => row && typeof row === 'object')
    .map((row, index) => {
      const quantity = moneyNumber(row.quantity || 1) || 1
      const unitCost = moneyNumber(row.unit_cost_snapshot ?? row.unit_cost ?? row.cost_price ?? row.cost)
      const unitPrice = moneyNumber(row.unit_sell_price_snapshot ?? row.unit_price ?? row.sell_price ?? row.price)
      const recordedGross = row.vat_price_snapshot?.tax?.gross_minor
      const customerDescription = String(row.customer_description || '').trim()
      const stockName = String(row.stock_name_snapshot || row.name || '').trim()
      return {
        id: row.id || `stock-usage-${index}`,
        name: customerDescription || stockName || 'Stock item',
        part_number: String(row.stock_internal_code_snapshot || row.stock_supplier_part_number_snapshot || row.part_number || row.partNumber || row.code || '').trim(),
        supplier: String(row.stock_supplier_snapshot || row.supplier || '').trim(),
        quantity,
        unit_type: String(row.unit_type || 'each').trim() || 'each',
        unit_cost: unitCost,
        unit_price: unitPrice,
        status: String(row.status || 'draft').trim().toLowerCase(),
        charged_to_customer: booleanValue(row.charged_to_customer),
        source_type: String(row.source_type || '').trim(),
        stock_item_id: String(row.stock_item_id || '').trim(),
        additional_work_request_id: String(row.additional_work_request_id || '').trim(),
        extra_service_line_request_id: String(row.extra_service_line_request_id || row.service_line_request_id || '').trim(),
        stock_usage_id: String(row.id || row.stock_usage_id || row.job_stock_usage_id || '').trim(),
        ...(Number.isSafeInteger(recordedGross) ? { gross_amount: recordedGross / 100 } : {}),
        confirmed_at: String(row.confirmed_at || '').trim(),
      }
    })
    .filter(row => row.name || row.part_number || row.supplier || row.unit_cost || row.unit_price)
}

function isBillableStockUsage(row = {}) {
  return row.status === 'confirmed_used'
    && row.charged_to_customer
    && row.unit_price > 0
}

export function isDirectJobStockUsage(row = {}) {
  const sourceType = String(row.source_type || '').trim().toLowerCase()
  return !row.additional_work_request_id
    && !row.extra_service_line_request_id
    && !['general_additional_work', 'additional_work', 'general_extra_service_line', 'extra_service_line', 'suspension_extra_service_line'].includes(sourceType)
}

export function unresolvedChargeableStockUsageRows(value) {
  return normaliseJobStockUsage(value)
    .filter(row => row.status === 'draft')
    .filter(row => row.charged_to_customer)
    .filter(isDirectJobStockUsage)
}

export function jobStockUsageTotals(value) {
  const stockUsed = normaliseJobStockUsage(value).filter(isBillableStockUsage)
  const costTotal = stockUsed.reduce((sum, row) => sum + (row.quantity * row.unit_cost), 0)
  const sellTotal = stockUsed.reduce((sum, row) => sum + (row.gross_amount ?? row.quantity * row.unit_price), 0)
  return { stockUsed, costTotal, sellTotal }
}

function directJobStockUsageTotals(value) {
  const stockUsed = normaliseJobStockUsage(value)
    .filter(isBillableStockUsage)
    .filter(isDirectJobStockUsage)
  const costTotal = stockUsed.reduce((sum, row) => sum + (row.quantity * row.unit_cost), 0)
  const sellTotal = stockUsed.reduce((sum, row) => sum + (row.gross_amount ?? row.quantity * row.unit_price), 0)
  return { stockUsed, costTotal, sellTotal }
}

export function isStockMirrorPartRow(row = {}) {
  const id = String(row.id || '').trim().toLowerCase()
  const source = String(row.source_type || row.source || '').trim().toLowerCase()
  return id.startsWith('stock-')
    || source === 'stock_usage'
    || Boolean(row.stock_usage_id || row.job_stock_usage_id || row.stock_item_id)
}

function timestampMs(value) {
  if (!value) return 0
  const time = new Date(value).getTime()
  return Number.isFinite(time) ? time : 0
}

function stockUsageAlreadyInStoredTotal(job = {}, row = {}) {
  const jobUpdatedAt = timestampMs(job.updated_at)
  const stockConfirmedAt = timestampMs(row.confirmed_at)
  return Boolean(jobUpdatedAt && stockConfirmedAt && jobUpdatedAt >= stockConfirmedAt)
}

function roundMoney(value) {
  return Math.round((Number(value) || 0) * 100) / 100
}

function jobServiceSnapshotTotal(value) {
  return normaliseJobServiceSnapshot(value).reduce((sum, service) => {
    const amount = moneyNumber(service?.base_price)
    return sum + (amount > 0 ? amount : 0)
  }, 0)
}

export function normalisePriceAdjustments(job = {}) {
  const rows = job.job_price_adjustments || job.price_adjustments || job.adjustments || []
  if (!Array.isArray(rows)) return []
  return rows
    .filter(row => row && typeof row === 'object')
    .map(row => {
      const targetLines = Array.isArray(row.target_lines) ? row.target_lines : []
      return {
        ...row,
        kind: ['discount', 'surcharge'].includes(row.kind) ? row.kind : 'discount',
        value_type: ['fixed', 'percent'].includes(row.value_type) ? row.value_type : 'fixed',
        value: moneyNumber(row.value),
        calculated_amount: moneyNumber(row.calculated_amount),
        status: ['pending', 'approved', 'rejected', 'voided'].includes(row.status) ? row.status : 'pending',
        target_scope: row.target_scope === 'job' ? 'job' : 'selected_service_lines',
        target_lines: targetLines,
        label: String(row.label || '').trim(),
        reason: String(row.reason || '').trim(),
      }
    })
}

function targetLinesTotal(lines = []) {
  return lines.reduce((sum, line) => {
    const lineTotal = moneyNumber(line.line_total ?? line.total ?? line.base_price ?? line.amount)
    const quantity = moneyNumber(line.quantity || 1) || 1
    const basePrice = moneyNumber(line.base_price)
    return sum + (lineTotal || (basePrice * quantity))
  }, 0)
}

export function priceAdjustmentAmount(adjustment = {}, subtotal = 0) {
  const kind = adjustment.kind === 'surcharge' ? 'surcharge' : 'discount'
  const valueType = adjustment.value_type === 'percent' ? 'percent' : 'fixed'
  const value = Math.max(0, moneyNumber(adjustment.value))
  const base = adjustment.target_scope === 'job'
    ? Math.max(0, subtotal)
    : Math.max(0, targetLinesTotal(adjustment.target_lines))

  let amount = valueType === 'percent' ? base * (Math.min(value, 100) / 100) : value
  if (kind === 'discount') {
    const cap = adjustment.target_scope === 'job' ? Math.max(0, subtotal) : base
    amount = Math.min(amount, cap)
  }
  return roundMoney(Math.max(0, amount))
}

export function jobFinancials(job = {}) {
  if (isGeneralJob(job)) return generalJobFinancials(job)
  const inheritedVat = normaliseJobServiceSnapshot(job.service_snapshot).some(line => line?.vat_source === true)

  const allStructuredParts = jobPartsTotals(job.parts_used)
  const stockUsageLoaded = job.job_stock_usage !== undefined && job.job_stock_usage !== null
  const structuredParts = jobPartsTotals(stockUsageLoaded
    ? allStructuredParts.partsUsed.filter(row => !isStockMirrorPartRow(row))
    : allStructuredParts.partsUsed)
  const stockUsage = stockUsageLoaded ? jobStockUsageTotals(job.job_stock_usage) : { stockUsed: [], costTotal: 0, sellTotal: 0 }
  const unresolvedChargeableStockCount = stockUsageLoaded
    ? unresolvedChargeableStockUsageRows(job.job_stock_usage).length
    : 0
  const legacyPartsCost = moneyNumber(job.parts_cost)
  const basePartsCost = (allStructuredParts.partsUsed.length ? structuredParts.costTotal : legacyPartsCost) + stockUsage.costTotal
  const partsSellTotal = structuredParts.sellTotal + stockUsage.sellTotal
  const storedTotal = moneyNumber(job.total_charged)
  const quoteAmount = moneyNumber(job.estimate_amount)
  const quotePartsCost = moneyNumber(job.estimate_parts_cost)
  const labourCharge = moneyNumber(job.labour_charge)
  const summaryServiceCost = serviceSummaryTotal(job.service_summary)
  const extraServiceLineRows = Array.isArray(job.job_extra_service_line_requests) ? job.job_extra_service_line_requests : []
  const extraServiceLineTotal = extraServiceLineBillableTotal(extraServiceLineRows)
  const stockAlreadyInStoredTotal = stockUsage.stockUsed
    .filter(row => stockUsageAlreadyInStoredTotal(job, row))
    .reduce((sum, row) => sum + (row.gross_amount ?? row.quantity * row.unit_price), 0)
  const storedFallbackAddons = quoteAmount + extraServiceLineTotal + stockAlreadyInStoredTotal
  const serviceCost = labourCharge > 0
    ? labourCharge
    : summaryServiceCost > 0
      ? summaryServiceCost
      : !storedTotal
        ? 0
        : Math.max(storedTotal - storedFallbackAddons, 0)
  const quoteDeclined = String(job.customer_decision || '').toLowerCase() === 'declined'
  const hasQuoteActivity = Boolean(job.estimate_sent_at || job.quote_last_sent_at || job.customer_decision)
  const looksLikeLegacyFullEstimate = !hasQuoteActivity
    && quoteAmount > 0
    && serviceCost > 0
    && Math.abs(quoteAmount - serviceCost) < 0.005
    && Math.abs(storedTotal - serviceCost) < 0.005
  const effectiveQuoteAmount = quoteDeclined || looksLikeLegacyFullEstimate ? 0 : quoteAmount
  const effectiveQuotePartsCost = quoteDeclined || looksLikeLegacyFullEstimate ? 0 : quotePartsCost
  const calculatedSubtotal = serviceCost + effectiveQuoteAmount + partsSellTotal + extraServiceLineTotal
  const subtotal = calculatedSubtotal > 0 ? calculatedSubtotal : storedTotal
  const adjustments = normalisePriceAdjustments(job)
  const approvedAdjustments = adjustments
    .filter(row => row.status === 'approved')
    .map(row => ({ ...row, calculatedAmount: inheritedVat ? row.calculated_amount : priceAdjustmentAmount(row, subtotal) }))
  const pendingAdjustments = adjustments
    .filter(row => row.status === 'pending')
    .map(row => ({ ...row, calculatedAmount: inheritedVat ? row.calculated_amount : priceAdjustmentAmount(row, subtotal) }))
  const approvedDiscountTotal = approvedAdjustments
    .filter(row => row.kind === 'discount')
    .reduce((sum, row) => sum + row.calculatedAmount, 0)
  const approvedSurchargeTotal = approvedAdjustments
    .filter(row => row.kind === 'surcharge')
    .reduce((sum, row) => sum + row.calculatedAmount, 0)
  const pendingDiscountTotal = pendingAdjustments
    .filter(row => row.kind === 'discount')
    .reduce((sum, row) => sum + row.calculatedAmount, 0)
  const pendingSurchargeTotal = pendingAdjustments
    .filter(row => row.kind === 'surcharge')
    .reduce((sum, row) => sum + row.calculatedAmount, 0)
  const totalCost = roundMoney(Math.max(0, subtotal - approvedDiscountTotal + approvedSurchargeTotal))
  const partsCost = basePartsCost + effectiveQuotePartsCost
  const grossMargin = totalCost - partsCost
  const marginPercent = totalCost > 0 ? (grossMargin / totalCost) * 100 : 0

  return {
    basePartsCost,
    partsCost,
    serviceCost,
    quoteAmount,
    quotePartsCost,
    partsSellTotal,
    partsUsed: [...structuredParts.partsUsed, ...stockUsage.stockUsed],
    stockPartsCost: roundMoney(stockUsage.costTotal),
    stockPartsSellTotal: roundMoney(stockUsage.sellTotal),
    effectiveQuoteAmount,
    effectiveQuotePartsCost,
    extraServiceLineTotal: roundMoney(extraServiceLineTotal),
    subtotal,
    approvedAdjustments,
    pendingAdjustments,
    approvedDiscountTotal,
    approvedSurchargeTotal,
    pendingDiscountTotal,
    pendingSurchargeTotal,
    totalCost,
    grossMargin,
    marginPercent,
    unresolvedChargeableStockCount,
  }
}

function generalJobFinancials(job = {}) {
  const inheritedVat = normaliseJobServiceSnapshot(job.service_snapshot).some(line => line?.vat_source === true)
  const attributedAdjustments = normalisePriceAdjustments(job).filter(row => inheritedVat || row.general_discount_id)
  const approvedAdjustments = attributedAdjustments.filter(row => row.status === 'approved').map(row => ({...row, calculatedAmount:row.calculated_amount}))
  const pendingAdjustments = attributedAdjustments.filter(row => row.status === 'pending').map(row => ({...row, calculatedAmount:row.calculated_amount}))
  const approvedDiscountTotal = roundMoney(approvedAdjustments.filter(row => row.kind === 'discount').reduce((sum,row)=>sum+row.calculatedAmount,0))
  const storedTotal = moneyNumber(job.total_charged)
  const labourCharge = moneyNumber(job.labour_charge)
  const stockUsageLoaded = job.job_stock_usage !== undefined && job.job_stock_usage !== null
  const stockUsage = stockUsageLoaded ? directJobStockUsageTotals(job.job_stock_usage) : { stockUsed: [], costTotal: 0, sellTotal: 0 }
  const unresolvedChargeableStockCount = stockUsageLoaded
    ? unresolvedChargeableStockUsageRows(job.job_stock_usage).length
    : 0
  const snapshotServiceCost = jobServiceSnapshotTotal(job.service_snapshot)
  const summaryServiceCost = serviceSummaryTotal(job.service_summary)
  const serviceCost = labourCharge > 0
    ? labourCharge
    : snapshotServiceCost > 0
      ? snapshotServiceCost
      : summaryServiceCost
  const additionalWorkRows = Array.isArray(job.job_additional_work_requests)
    ? job.job_additional_work_requests
    : Array.isArray(job.additional_work)
      ? job.additional_work
      : null
  const extraServiceLineRows = Array.isArray(job.job_extra_service_line_requests)
    ? job.job_extra_service_line_requests
    : Array.isArray(job.extra_service_lines)
      ? job.extra_service_lines
      : null
  const additionalWorkTotal = additionalWorkRows
    ? additionalWorkBillableTotal(additionalWorkRows)
    : 0
  const extraServiceLineTotal = extraServiceLineRows
    ? extraServiceLineBillableTotal(extraServiceLineRows)
    : 0
  const loadedExtrasTotal = roundMoney(additionalWorkTotal + extraServiceLineTotal + stockUsage.sellTotal)
  const hasLoadedExtras = Boolean(additionalWorkRows || extraServiceLineRows || stockUsageLoaded)
  const effectiveExtrasTotal = hasLoadedExtras
    ? loadedExtrasTotal
    : Math.max(storedTotal + approvedDiscountTotal - serviceCost, 0)
  const calculatedTotal = roundMoney(Math.max(serviceCost + effectiveExtrasTotal, 0))
  const totalCost = inheritedVat ? storedTotal : hasLoadedExtras
    ? roundMoney(Math.max(calculatedTotal - approvedDiscountTotal, 0))
    : roundMoney(Math.max(calculatedTotal - approvedDiscountTotal, storedTotal, 0))
  const partsCost = roundMoney(stockUsage.costTotal)
  const grossMargin = roundMoney(totalCost - partsCost)
  const marginPercent = totalCost > 0 ? (grossMargin / totalCost) * 100 : 0

  return {
    basePartsCost: partsCost,
    partsCost,
    serviceCost: roundMoney(serviceCost),
    quoteAmount: 0,
    quotePartsCost: 0,
    partsSellTotal: roundMoney(stockUsage.sellTotal),
    partsUsed: stockUsage.stockUsed,
    stockPartsCost: partsCost,
    stockPartsSellTotal: roundMoney(stockUsage.sellTotal),
    effectiveQuoteAmount: 0,
    effectiveQuotePartsCost: 0,
    subtotal: calculatedTotal,
    approvedAdjustments,
    pendingAdjustments,
    approvedDiscountTotal,
    approvedSurchargeTotal: 0,
    pendingDiscountTotal: roundMoney(pendingAdjustments.reduce((sum, row) => sum + row.calculatedAmount, 0)),
    pendingSurchargeTotal: 0,
    additionalWorkTotal: roundMoney(additionalWorkRows ? additionalWorkTotal : (!extraServiceLineRows && !stockUsageLoaded ? effectiveExtrasTotal : additionalWorkTotal)),
    extraServiceLineTotal: roundMoney(extraServiceLineTotal),
    billableExtrasTotal: roundMoney(effectiveExtrasTotal),
    totalCost,
    grossMargin,
    marginPercent,
    unresolvedChargeableStockCount,
  }
}

function invoiceText(value) {
  return String(value || '')
    .replace(/(?:GBP|£)\s*[0-9]+(?:\.[0-9]{1,2})?/gi, '')
    .replace(/\s+/g, ' ')
    .replace(/\s*[-–—]\s*$/g, '')
    .trim()
}

function invoiceLine(type, description, amount, options = {}) {
  const quantity = moneyNumber(options.quantity || 1) || 1
  const lineTotal = roundMoney(amount)
  return {
    type,
    description: invoiceText(description) || options.fallback || 'Workshop item',
    quantity,
    unit_price: roundMoney(options.unit_price ?? (quantity ? lineTotal / quantity : lineTotal)),
    line_total: lineTotal,
  }
}

function serviceInvoiceDescription(job = {}) {
  const template = jobServiceTemplateName(job)
  if (template) return template
  const summary = invoiceText(jobServiceSummary(job) || job.service_summary)
  if (summary) return summary
  return isGeneralJob(job) ? 'General workshop services' : 'Suspension service'
}

function serviceInvoiceLines(job = {}, financials = {}) {
  const serviceCost = roundMoney(financials.serviceCost)
  if (serviceCost <= 0) return []

  const snapshotRows = normaliseJobServiceSnapshot(job.service_snapshot)
    .map((service, index) => ({
      id: service?.id || `service-${index + 1}`,
      name: invoiceText(service?.name),
      category: invoiceText(service?.category_label || service?.category),
      amount: moneyNumber(service?.base_price),
    }))
    .filter(service => service.name && service.amount > 0)
  const snapshotTotal = roundMoney(snapshotRows.reduce((sum, service) => sum + service.amount, 0))

  if (isGeneralJob(job) && snapshotRows.length && Math.abs(snapshotTotal - serviceCost) < 0.005) {
    return snapshotRows.map(service => invoiceLine(
      'service',
      service.category ? `${service.name} (${service.category})` : service.name,
      service.amount
    ))
  }

  return [invoiceLine('service', serviceInvoiceDescription(job), serviceCost, {
    fallback: isGeneralJob(job) ? 'General workshop services' : 'Suspension service',
  })]
}

function additionalWorkInvoiceLines(job = {}) {
  if (!isGeneralJob(job)) return []
  const rows = Array.isArray(job.job_additional_work_requests)
    ? job.job_additional_work_requests
    : Array.isArray(job.additional_work)
      ? job.additional_work
      : []

  return rows
    .filter(row => isBillableAdditionalWorkStatus(row.status))
    .map(row => {
      const visible = row.customer_visible !== false
      const description = visible
        ? invoiceText(row.title || row.customer_explanation)
        : 'Bespoke additional work'
      return invoiceLine('bespoke_work', description, additionalWorkPrice(row), { fallback: 'Bespoke additional work' })
    })
    .filter(line => line.line_total > 0)
}

function extraServiceInvoiceLines(job = {}) {
  const rows = Array.isArray(job.job_extra_service_line_requests)
    ? job.job_extra_service_line_requests
    : Array.isArray(job.extra_service_lines)
      ? job.extra_service_lines
      : []

  return rows
    .filter(row => isBillableExtraServiceLineStatus(row.status))
    .map(row => {
      const name = invoiceText(row.service_snapshot?.name || row.service_name || row.title)
      const category = invoiceText(row.service_snapshot?.category_label || row.service_snapshot?.category || row.service_category)
      return invoiceLine(
        'extra_service',
        category ? `${name} (${category})` : name,
        extraServiceLinePrice(row),
        { fallback: isGeneralJob(job) ? 'General extra service' : 'Suspension extra service' }
      )
    })
    .filter(line => line.line_total > 0)
}

function bespokeQuoteInvoiceLines(job = {}, financials = {}) {
  if (isGeneralJob(job)) return []
  const amount = roundMoney(financials.effectiveQuoteAmount)
  if (amount <= 0) return []
  return [invoiceLine('bespoke_work', invoiceText(job.estimate_notes) || 'Bespoke Additional Quote', amount, {
    fallback: 'Bespoke Additional Quote',
  })]
}

function stockMaterialInvoiceLines(financials = {}) {
  const rows = Array.isArray(financials.partsUsed) ? financials.partsUsed : []
  const lines = rows
    .map(part => {
      const quantity = moneyNumber(part.quantity || 1) || 1
      const unitPrice = moneyNumber(part.unit_price)
      const lineTotal = roundMoney(part.gross_amount ?? quantity * unitPrice)
      const code = invoiceText(part.part_number)
      const description = code
        ? `${invoiceText(part.name || 'Parts / materials')} (${code})`
        : invoiceText(part.name || 'Parts / materials')
      return invoiceLine('stock_material', description, lineTotal, { quantity, unit_price: part.gross_amount !== undefined ? 0 : unitPrice, fallback: 'Parts / materials' })
    })
    .filter(line => line.line_total > 0)

  const lineTotal = roundMoney(lines.reduce((sum, line) => sum + line.line_total, 0))
  const expectedTotal = roundMoney(financials.partsSellTotal)
  if (!lines.length && expectedTotal > 0) return [invoiceLine('stock_material', 'Parts / materials', expectedTotal)]
  if (Math.abs(lineTotal - expectedTotal) > 0.005 && expectedTotal > 0) {
    return [invoiceLine('stock_material', 'Parts / materials', expectedTotal)]
  }
  return lines
}

function adjustmentInvoiceLines(financials = {}) {
  return (Array.isArray(financials.approvedAdjustments) ? financials.approvedAdjustments : [])
    .map(row => {
      const kind = row.kind === 'surcharge' ? 'adjustment' : 'discount'
      const amount = roundMoney(row.calculatedAmount)
      const signedAmount = kind === 'discount' ? -amount : amount
      return invoiceLine(kind, row.label || (kind === 'discount' ? 'Discount' : 'Adjustment'), signedAmount, {
        fallback: kind === 'discount' ? 'Discount' : 'Adjustment',
      })
    })
    .filter(line => Math.abs(line.line_total) > 0)
}

function customerInvoiceTitle(job = {}) {
  const paid = String(job.payment_status || '').trim().toLowerCase() === 'paid' || Boolean(job.payment_paid_at)
  return paid ? 'Itemised receipt' : 'Itemised invoice'
}

export function customerInvoiceBreakdown(job = {}, financials = jobFinancials(job)) {
  if (job.vat_document) return invoiceFromVatDocument(job)
  const baseLines = [
    ...serviceInvoiceLines(job, financials),
    ...extraServiceInvoiceLines(job),
    ...additionalWorkInvoiceLines(job),
    ...bespokeQuoteInvoiceLines(job, financials),
    ...stockMaterialInvoiceLines(financials),
  ]
  const baseTotal = roundMoney(baseLines.reduce((sum, line) => sum + line.line_total, 0))
  const expectedSubtotal = roundMoney(financials.subtotal)
  const subtotalGap = roundMoney(expectedSubtotal - baseTotal)
  const fallbackLines = subtotalGap > 0.005
    ? [invoiceLine(isGeneralJob(job) ? 'bespoke_work' : 'adjustment', 'Approved additional work', subtotalGap)]
    : []
  const adjustmentLines = adjustmentInvoiceLines(financials)
  const lines = [...baseLines, ...fallbackLines, ...adjustmentLines]
  const lineTotal = roundMoney(lines.reduce((sum, line) => sum + line.line_total, 0))

  return {
    title: customerInvoiceTitle(job),
    currency: 'GBP',
    lines,
    subtotal: expectedSubtotal,
    discounts: roundMoney(financials.approvedDiscountTotal),
    adjustments: roundMoney(financials.approvedSurchargeTotal),
    total: roundMoney(financials.totalCost),
    line_total: lineTotal,
  }
}

export function jobServiceTemplateName(job = {}) {
  const snapshot = job.service_template_snapshot
  if (!snapshot) return ''
  if (typeof snapshot === 'object') return snapshot.name || ''
  try {
    const parsed = JSON.parse(snapshot)
    return parsed?.name || ''
  } catch {
    return ''
  }
}

function normaliseTemplateArray(value) {
  if (!value) return []
  if (Array.isArray(value)) return value
  try {
    const parsed = JSON.parse(value)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function normaliseObject(value) {
  if (!value) return {}
  if (typeof value === 'object' && !Array.isArray(value)) return value
  try {
    const parsed = JSON.parse(value)
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
  } catch {
    return {}
  }
}

function serviceTemplateSnapshot(job = {}) {
  const snapshot = job.service_template_snapshot
  if (!snapshot) return null
  if (typeof snapshot === 'object') return snapshot
  try {
    return JSON.parse(snapshot)
  } catch {
    return null
  }
}

export function jobMeasurementSections(job = {}, category = null) {
  const snapshot = serviceTemplateSnapshot(job)
  const sections = normaliseTemplateArray(snapshot?.measurement_schema)
    .filter(section => section && typeof section === 'object')
    .map(section => ({
      key: section.key || section.title || 'measurement_section',
      title: section.title || 'Measurements',
      category: section.category || 'service_log',
      fields: normaliseTemplateArray(section.fields).filter(field => field && typeof field === 'object'),
    }))
    .filter(section => section.fields.length > 0)

  return category ? sections.filter(section => section.category === category) : sections
}

function measurementValue(job, field, side = null) {
  if (side === 'before' && field.before) return job[field.before] || ''
  if (side === 'after' && field.after) return job[field.after] || ''
  if (field.field) return job[field.field] || ''
  return normaliseObject(job.structured_measurements)[field.key] || ''
}

function formatMeasurementValue(job, field, side = null) {
  const value = measurementValue(job, field, side)
  if (!value) return ''
  return field.unit ? `${value} ${field.unit}` : value
}

export function jobMeasurementRows(job = {}, category = null, customerOnly = false) {
  return jobMeasurementSections(job, category).map(section => {
    const rows = section.fields
      .filter(field => !customerOnly || (field.visibility !== 'internal' && field.customer_visible !== false))
      .map(field => {
        const value = field.kind === 'before_after'
          ? `${formatMeasurementValue(job, field, 'before') || '-'} -> ${formatMeasurementValue(job, field, 'after') || '-'}`
          : formatMeasurementValue(job, field)
        const hasValue = field.kind === 'before_after'
          ? Boolean(measurementValue(job, field, 'before') || measurementValue(job, field, 'after'))
          : Boolean(measurementValue(job, field))
        return {
          key: field.key || field.field || field.label,
          label: field.unit && field.kind !== 'before_after' ? `${field.label || field.key} (${field.unit})` : (field.label || field.key),
          value,
          hasValue,
          customerVisible: field.visibility !== 'internal' && field.customer_visible !== false,
        }
      })
      .filter(row => row.hasValue)

    return { key: section.key, title: section.title, category: section.category, rows }
  }).filter(section => section.rows.length > 0)
}

export function rockShoxTrailheadUrl(serial = '') {
  const cleanSerial = String(serial || '').trim()
  return cleanSerial
    ? `https://trailhead.rockshox.com/en/product/${encodeURIComponent(cleanSerial)}`
    : 'https://trailhead.rockshox.com/en/product/search/serial'
}

export function hl(str, term) {
  if (!term || !str) return escapeHtml(str || '')
  const lower = str.toLowerCase()
  const lterm = term.toLowerCase()
  let result = ''
  let i = 0
  while (i < str.length) {
    const pos = lower.indexOf(lterm, i)
    if (pos === -1) { result += escapeHtml(str.slice(i)); break }
    result += escapeHtml(str.slice(i, pos)) + '<span class="highlight">' + escapeHtml(str.slice(pos, pos + term.length)) + '</span>'
    i = pos + term.length
  }
  return result
}

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
}

function escapeObject(value) {
  if (typeof value === 'string') return escapeHtml(value)
  if (Array.isArray(value)) return value.map(escapeObject)
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([key, val]) => [key, escapeObject(val)]))
  }
  return value
}

function bookingNoteLines(job) {
  return String(job?.notes || '').split('\n').map(line => line.trim()).filter(Boolean)
}

function bookingNoteSection(lines, prefix) {
  const idx = lines.findIndex(line => line.toLowerCase().startsWith(prefix.toLowerCase()))
  if (idx === -1) return []

  const values = []
  const first = lines[idx].slice(prefix.length).trim()
  if (first) values.push(first)

  for (let i = idx + 1; i < lines.length; i += 1) {
    if (/^(Email|Phone|Bike|Component|Services|Notes):/i.test(lines[i])) break
    values.push(lines[i])
  }

  return values
}

function normaliseComponentBrand(make, componentType = '') {
  const value = String(make || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '')
  if (value.includes('rockshox')) return 'rockshox'
  if (value.includes('sram')) return componentType === 'brake' ? 'sram' : 'rockshox'
  if (value.includes('shimano')) return 'shimano'
  if (value.includes('magura')) return 'magura'
  if (value.includes('hope')) return 'hope'
  if (value.includes('trp')) return 'trp'
  if (value.includes('hayes')) return 'hayes'
  if (value.includes('fox')) return 'fox'
  if (value.includes('ohlins')) return 'ohlins'
  if (value.includes('marzocchi')) return 'marzocchi'
  return 'other'
}

function inferComponentType(details) {
  const value = String(details || '').toLowerCase()
  if (/(brake|bleed|caliper|lever|rotor|pad|hose|mineral oil|dot fluid|maven|code rsc|guide|level|g2|saint|zee|deore|slx|xt|xtr|mt5|mt7|tech 4|dominion)/.test(value)) return 'brake'
  if (/(shock|super deluxe|deluxe|vivid|float x|dhx|coil|rear)/.test(value)) return 'shock'
  if (/(dropper|reverb|seatpost)/.test(value)) return 'dropper'
  if (/(fork|zeb|pike|lyrik|sid|boxxer|domain|reba|recon|35|36|38|40)/.test(value)) return 'fork'
  return 'other'
}

export function bookingJobSummary(job) {
  const lines = bookingNoteLines(job)
  if (!lines.length) return ''

  function collect(prefix) {
    return bookingNoteSection(lines, prefix).join(' - ')
  }

  return collect('Component:')
    || collect('Services:')
    || collect('Bike:')
    || lines.find(line => !line.startsWith('Booking request from'))
    || lines[0]
}

export function normaliseJobServiceSnapshot(value) {
  if (Array.isArray(value)) return value
  if (!value || typeof value !== 'string') return []
  try {
    const parsed = JSON.parse(value)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

export function jobServiceLines(job) {
  const explicit = String(job?.service_summary || '')
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)
  if (explicit.length) return explicit

  const snapshotLines = normaliseJobServiceSnapshot(job?.service_snapshot)
    .map(service => String(service?.name || '').trim())
    .filter(Boolean)
  if (snapshotLines.length) return snapshotLines

  const lines = bookingNoteLines(job)
  if (!lines.length) return []
  return bookingNoteSection(lines, 'Services:')
}

export function jobServiceSummary(job) {
  return jobServiceLines(job).join(' - ')
}

export function jobServiceScopeSummary(job = {}) {
  const original = jobServiceSummary(job)
  const extras = extraServiceLineJobScopeSummary(job.job_extra_service_line_requests || [])
  return [original, extras].filter(Boolean).join(' - ')
}

export const JOB_STATUS_OPTIONS = [
  ['awaiting_customer_item', 'Awaiting Item From Customer'],
  ['received', 'Received'],
  ['estimate_sent', 'Quote Sent - Waiting Approval'],
  ['awaiting_parts', 'Awaiting parts'],
  ['ready_for_collection', 'Ready for Collection'],
  ['completed', 'Completed'],
  ['cancelled', 'Cancelled'],
]

const JOB_STATUS_META = {
  booking_request: { label: 'Booking request', tone: 'orange', nextAction: 'Review booking' },
  awaiting_customer_item: { label: 'Awaiting Item From Customer', tone: 'orange', nextAction: 'Wait for customer to bring the item in' },
  received: { label: 'Received', tone: 'blue', nextAction: 'Work through the job card' },
  estimate_sent: { label: 'Quote Sent - Waiting Approval', tone: 'orange', nextAction: 'Wait for customer approval' },
  awaiting_parts: { label: 'Awaiting parts', tone: 'orange', nextAction: 'Fit parts when available' },
  ready_for_collection: { label: 'Ready for Collection', tone: 'green', nextAction: 'Customer collection' },
  completed: { label: 'Completed', tone: 'green', nextAction: 'Job complete' },
  cancelled: { label: 'Cancelled', tone: 'muted', nextAction: 'No action' },
}

const LEGACY_JOB_STATUS_MAP = {
  awaiting_inspection: 'received',
  inspected: 'received',
  estimate_required: 'received',
  approved: 'received',
  in_progress: 'received',
  qc_required: 'received',
  ready_for_report: 'received',
  report_sent: 'ready_for_collection',
  awaiting_payment: 'ready_for_collection',
  collected: 'completed',
  closed: 'completed',
}

export function normaliseJobStatus(value) {
  const status = String(value || '').trim()
  return LEGACY_JOB_STATUS_MAP[status] || status || 'received'
}

export function jobStatusMeta(job) {
  const status = normaliseJobStatus(job?.status)
  if (JOB_STATUS_META[status]) {
    if (isGeneralJob(job) && status === 'received') {
      return {
        ...JOB_STATUS_META[status],
        nextAction: generalJobDormantMessage(),
      }
    }
    if (status === 'ready_for_collection') {
      const payment = jobPaymentMeta(job)
      return {
        ...JOB_STATUS_META[status],
        nextAction: payment.key === 'paid' ? 'Complete handover and close job' : 'Take payment before collection',
      }
    }
    if (status === 'completed' && job?.closed_at) {
      return {
        ...JOB_STATUS_META[status],
        nextAction: `Closed ${new Date(job.closed_at).toLocaleDateString('en-GB')}`,
      }
    }
    return JOB_STATUS_META[status]
  }
  if (isGeneralJob(job)) {
    return { label: 'General job', tone: 'blue', nextAction: generalJobDormantMessage() }
  }
  if (!job?.component_id) return { label: 'Needs component', tone: 'orange', nextAction: 'Create or assign component' }
  if (!job?.technician) return { label: 'Unassigned tech', tone: 'muted', nextAction: 'Assign technician' }
  return JOB_STATUS_META.received
}

const JOB_PAYMENT_META = {
  not_requested: { key: 'not_requested', label: 'Not recorded', tone: 'muted', nextAction: 'Request or record payment' },
  requested: { key: 'requested', label: 'Waiting', tone: 'orange', nextAction: 'Send reminder if overdue' },
  paid: { key: 'paid', label: 'Paid', tone: 'green', nextAction: 'No action' },
  failed: { key: 'failed', label: 'Failed', tone: 'red', nextAction: 'Review payment status' },
  requires_review: { key: 'requires_review', label: 'Needs review', tone: 'orange', nextAction: 'Review Stripe payment before closing' },
  cancelled: { key: 'cancelled', label: 'Cancelled', tone: 'muted', nextAction: 'No action' },
}

export const JOB_PAYMENT_OPTIONS = [
  ['all', 'Any payment status'],
  ['not_requested', 'Payment not sent'],
  ['requested', 'Waiting for payment'],
  ['paid', 'Paid'],
  ['failed', 'Failed'],
  ['requires_review', 'Needs review'],
  ['cancelled', 'Cancelled'],
]

export function currentCustomerPaymentLink(job = {}) {
  if (job.payment_paid_at || ['paid', 'requires_review', 'failed', 'cancelled'].includes(job.payment_status)
    || ['cancelled', 'voided', 'closed', 'completed', 'collected'].includes(job.status)) return ''
  if (Date.parse(job.customer_payment_link_expires_at || '') <= Date.now()) return ''
  if (!Number.isFinite(Date.parse(job.customer_payment_link_expires_at || ''))) return ''
  const url = String(job.customer_payment_link || '')
  return /^https:\/\/[^/#?]+\/pay#[a-f0-9]{64}$/.test(url) ? url : ''
}

export function jobPaymentMeta(job = {}) {
  if (currentCustomerPaymentLink(job)) {
    return { ...JOB_PAYMENT_META.requested, label: 'Seven-day link active', nextAction: 'Await customer payment' }
  }
  if (job.payment_refresh_required && String(job.payment_refresh_reason || '').toLowerCase().includes('expired')) {
    return {
      ...JOB_PAYMENT_META.not_requested,
      label: 'Payment link expired',
      tone: 'orange',
      nextAction: 'Generate a new payment link',
    }
  }
  const explicit = String(job.payment_status || '').trim().toLowerCase()
  if (explicit && JOB_PAYMENT_META[explicit]) return JOB_PAYMENT_META[explicit]
  if (job.payment_paid_at) return JOB_PAYMENT_META.paid
  if (job.payment_link) return JOB_PAYMENT_META.requested
  return JOB_PAYMENT_META.not_requested
}

export function jobPaymentActionState(job = {}, financials = jobFinancials(job)) {
  const payment = jobPaymentMeta(job)
  const totalCost = moneyNumber(financials?.totalCost)
  const amountPence = Math.round(totalCost * 100)
  const status = normaliseJobStatus(job.status)
  const cancelled = ['cancelled', 'voided'].includes(status)
  const generalJob = isGeneralJob(job)
  const generalFinal = jobPriceStatus(job) === 'final'
  const storedAmountPence = Number(job.payment_amount_pence || 0)
  const amountMismatch = Boolean(
    payment.key !== 'paid'
    && storedAmountPence > 0
    && storedAmountPence !== amountPence
  )
  const needsRefresh = Boolean(job.payment_refresh_required || amountMismatch)
  const unresolvedChargeableStockCount = Number(financials?.unresolvedChargeableStockCount || 0)
  const customerLink = currentCustomerPaymentLink(job)
  const activePaymentLink = customerLink || (needsRefresh ? '' : String(job.payment_link || ''))
  let blockedReason = ''
  let refreshReason = ''

  if (cancelled) {
    blockedReason = 'Cancelled jobs cannot receive payment links.'
  } else if (payment.key === 'requires_review') {
    blockedReason = 'Review the Stripe payment state before creating another payment link.'
  } else if (unresolvedChargeableStockCount > 0) {
    blockedReason = `Resolve ${unresolvedChargeableStockCount} chargeable draft stock item${unresolvedChargeableStockCount === 1 ? '' : 's'} before requesting payment.`
  } else if (generalJob && !generalFinal) {
    blockedReason = 'Set General Workshop pricing to Final before requesting payment.'
  } else if (!amountPence || amountPence < 50) {
    blockedReason = generalJob
      ? 'Add a final billable total of at least GBP 0.50 before requesting payment.'
      : 'Amount must be at least GBP 0.50.'
  }

  if (job.payment_refresh_required) {
    refreshReason = customerLink
      ? 'The customer link is still active. Refresh checkout before sending another payment request.'
      : job.payment_refresh_reason || 'Generate a new payment link before requesting payment.'
  } else if (amountMismatch) {
    refreshReason = generalJob
      ? 'General Workshop job total has changed. Generate a new payment link before requesting payment.'
      : 'Suspension job total has changed. Generate a new payment link before requesting payment.'
  }

  return {
    payment,
    totalCost,
    amountPence,
    activePaymentLink,
    needsRefresh,
    refreshReason,
    blockedReason,
    canCreateLink: !job.active_payment_bundle_attempt_id && payment.key !== 'paid' && !blockedReason && (!activePaymentLink || needsRefresh),
    canCopyLink: payment.key !== 'paid' && !blockedReason && Boolean(activePaymentLink),
    canSendReminder: !job.active_payment_bundle_attempt_id && payment.key === 'requested' && !blockedReason && Boolean(activePaymentLink) && !needsRefresh,
  }
}

export function jobHasApprovedQuote(job = {}) {
  return String(job.customer_decision || '').toLowerCase() === 'approved'
}

export function jobProgressMeta(job = {}) {
  if (isGeneralJob(job)) return []

  const inspectionFields = [
    'inspection_completed_at', 'inspection_summary', 'condition_notes',
    'ins_stanchion', 'ins_csu', 'ins_bushing_play', 'ins_air_shaft', 'ins_damp_shaft',
    'ins_eyelet', 'ins_bearing', 'ins_wiper', 'ins_oil_contam', 'ins_scoring',
    'ins_adjusters', 'ins_lockout'
  ]
  const qcFields = [
    'qc_torque', 'qc_pressure', 'qc_clickers', 'qc_travel',
    'qc_topout', 'qc_oil_leak', 'qc_air_loss', 'qc_adjusters'
  ]
  const inspectionDone = inspectionFields.some(field => Boolean(job[field]))
  const qcStarted = qcFields.some(field => Boolean(job[field]))
  const qcChecksDone = qcFields.every(field => Boolean(job[field]))
  const qcStatus = String(job.qc_status || '').toLowerCase()
  const qcSignedOff = Boolean(job.qc_signed_off_at)
  const reportSent = Boolean(job.report_sent_at)
  const payment = jobPaymentMeta(job)
  let qcValue = 'Pending'
  let qcTone = 'muted'
  if (qcStatus === 'passed' || (qcSignedOff && qcStatus !== 'failed')) {
    qcValue = 'Signed off'
    qcTone = 'green'
  } else if (qcStatus === 'failed') {
    qcValue = 'Failed'
    qcTone = 'orange'
  } else if (qcChecksDone) {
    qcValue = 'Ready to sign'
    qcTone = 'orange'
  } else if (qcStarted) {
    qcValue = 'Started'
    qcTone = 'orange'
  }

  return [
    {
      key: 'inspection',
      label: 'Inspection',
      value: inspectionDone ? 'Inspected' : 'Awaiting',
      tone: inspectionDone ? 'green' : 'orange',
    },
    {
      key: 'qc',
      label: 'QC',
      value: qcValue,
      tone: qcTone,
    },
    {
      key: 'report',
      label: 'Report',
      value: reportSent ? 'Sent' : 'Not sent',
      tone: reportSent ? 'green' : 'muted',
    },
    {
      key: 'payment',
      label: 'Payment',
      value: payment.label,
      tone: payment.tone,
    },
  ]
}

export function bookingComponentDraft(job) {
  const lines = bookingNoteLines(job)
  const componentLines = bookingNoteSection(lines, 'Component:')
  const serviceLines = bookingNoteSection(lines, 'Services:')
  const details = [...componentLines, ...serviceLines].join(' ')
  const fields = {}
  const other = []

  componentLines.forEach(line => {
    const match = line.match(/^(Serial|Make|Model|Year):\s*(.*)$/i)
    if (!match) {
      other.push(line)
      return
    }
    fields[match[1].toLowerCase()] = match[2].trim()
  })

  const type = inferComponentType(details)

  return {
    type,
    brand: normaliseComponentBrand(fields.make, type),
    serial: fields.serial || '',
    model: fields.model || '',
    year: fields.year || '',
    notes: other.join('\n'),
  }
}

function safeImageUrl(value) {
  const url = String(value || '').trim()
  return /^https?:\/\//i.test(url) ? url : ''
}

function evidenceTypeLabel(value = '') {
  const key = String(value || '').trim()
  const labels = {
    photo: 'Photo',
    video: 'Video',
    file: 'File / Document',
    dyno_testing_report: 'Dyno Testing Report',
  }
  return labels[key] || key
}

function attr(value) {
  return String(value || '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;')
}

function reportBrandHtml(workshopName, logoUrl) {
  if (logoUrl) {
    return '<img class="report-logo" src="' + attr(logoUrl) + '" alt="' + attr(workshopName) + ' logo">'
  }
  const parts = String(workshopName || 'Workshop').split(' ')
  const last = parts.pop()
  const first = parts.join(' ')
  return '<div class="logo">' + (first ? first + ' <span>' + last + '</span>' : '<span>' + last + '</span>') + '</div>'
}

export function generateCustomerReport(job, customer, component, workshop = null) {
  if (isGeneralJob(job)) {
    if (typeof window !== 'undefined') {
      window.alert('Open the General job to preview, save or email the General Workshop report.')
    }
    return
  }

  const unresolvedStock = unresolvedChargeableStockUsageRows(job.job_stock_usage)
  if (unresolvedStock.length > 0) {
    if (typeof window !== 'undefined') {
      window.alert(`Resolve ${unresolvedStock.length} chargeable draft stock item${unresolvedStock.length === 1 ? '' : 's'} before creating an invoice or receipt.`)
    }
    return
  }

  const rawWorkshopLogoUrl = safeImageUrl(workshop?.logo_url)
  const rawServiceTemplateName = escapeHtml(jobServiceTemplateName(job))
  const structuredSettings = jobMeasurementRows(job, 'settings', true)
  const structuredServiceLog = jobMeasurementRows(job, 'service_log', true)
  const rawEvidenceItems = Array.isArray(job.evidence) ? job.evidence.filter(item => item.visibility === 'customer') : []
  const rawFinancials = jobFinancials(job)
  const rawPartsUsed = rawFinancials.partsUsed || []
  const rawInvoice = customerInvoiceBreakdown(job, rawFinancials)
  job = escapeObject(job)
  customer = escapeObject(customer)
  component = escapeObject(component)
  workshop = escapeObject(workshop)
  const financials = rawFinancials
  const partsUsed = rawPartsUsed.map(escapeObject)
  const invoice = rawInvoice

  const workshopName = workshop?.name || 'Torque Collective'
  const workshopLogoUrl = rawWorkshopLogoUrl
  const serviceTemplateName = rawServiceTemplateName
  const footerDetails = [workshopName, workshop?.address].filter(Boolean).join(' &nbsp;·&nbsp; ')
  const scopeItems = [
    ['Lower leg service', job.sc_lower_leg], ['Air spring service', job.sc_air_spring],
    ['Damper rebuild', job.sc_damper], ['Air can service', job.sc_air_can],
    ['Full shock service', job.sc_full_shock], ['Bushing / hardware replacement', job.sc_bushing],
    ['Travel change', job.sc_travel], ['Seal upgrade', job.sc_seal_upgrade],
    ['Custom tune / revalve', job.sc_retune], ['Setup session only', job.sc_setup],
    ['Seatpost / dropper service', job.sc_dropper_service]
  ].filter(([, v]) => v).map(([l]) => l)

  const qcStatus = String(job.qc_status || '').toLowerCase()
  const legacyQcPassed = job.qc_torque && job.qc_pressure && job.qc_clickers &&
    job.qc_travel && job.qc_topout && job.qc_oil_leak && job.qc_air_loss && job.qc_adjusters
  const qcPassed = qcStatus === 'passed' || (!qcStatus && legacyQcPassed)
  const qcFailed = qcStatus === 'failed'
  const qcSignedMeta = [job.qc_signed_off_by_name, job.qc_signed_off_at ? new Date(job.qc_signed_off_at).toLocaleString('en-GB') : ''].filter(Boolean).join(' - ')

  function srow(label, before, after) {
    if (!before && !after) return ''
    return '<tr>'
      + '<td style="padding:7px 12px 7px 0;color:#666;font-size:13px;width:220px">' + label + '</td>'
      + '<td style="padding:7px 8px;font-size:13px;color:#1a1a1a;width:40%">' + (before || '—') + '</td>'
      + '<td style="padding:7px 0;font-size:13px;color:#2d9e6b;font-weight:600">' + (after || '—') + '</td>'
      + '</tr>'
  }

  function qrow(label, val) {
    if (!val) return ''
    return '<div style="display:flex;gap:10px;align-items:center;padding:5px 0;border-bottom:1px solid #f0f0f0;font-size:13px;color:#1a1a1a">'
      + '<span style="color:#2d9e6b;font-size:15px;flex-shrink:0">✓</span>' + label + '</div>'
  }

  function measurementTable(sections) {
    return sections.map(section => '<div style="margin-bottom:14px">'
      + '<div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:#999;margin:0 0 6px">' + escapeHtml(section.title) + '</div>'
      + '<table><tbody>'
      + section.rows.map(row => '<tr>'
        + '<td style="padding:6px 12px 6px 0;color:#666;font-size:13px;width:220px">' + escapeHtml(row.label) + '</td>'
        + '<td style="padding:6px 0;font-size:13px;color:#1a1a1a">' + escapeHtml(row.value) + '</td>'
        + '</tr>').join('')
      + '</tbody></table></div>').join('')
  }

  function evidenceHtml(items) {
    return items.map(item => {
      const title = escapeHtml(item.caption || item.file_name)
      const meta = escapeHtml([item.stage, evidenceTypeLabel(item.evidence_type)].filter(Boolean).join(' - '))
      if (item.evidence_type === 'photo' && item.signed_url) {
        return '<div style="margin:0 0 14px;padding:12px;border:1px solid #eee;border-radius:6px">'
          + '<img src="' + escapeHtml(item.signed_url) + '" alt="' + title + '" style="display:block;max-width:100%;height:auto;border-radius:4px;margin-bottom:8px">'
          + '<div style="font-size:13px;font-weight:700">' + title + '</div>'
          + '<div style="font-size:11px;color:#777;margin-top:3px">' + meta + '</div>'
          + '</div>'
      }
      const link = item.signed_url
        ? '<a href="' + escapeHtml(item.signed_url) + '" style="color:#e85d04;font-weight:700">Open evidence file</a>'
        : ''
      return '<div style="margin:0 0 10px;padding:12px;border:1px solid #eee;border-radius:6px">'
        + '<div style="font-size:13px;font-weight:700">' + title + '</div>'
        + '<div style="font-size:11px;color:#777;margin:3px 0 8px">' + meta + '</div>'
        + link
      + '</div>'
    }).join('')
  }

  function partsUsedHtml(items) {
    return items.map(part => {
      const sellTotal = part.gross_amount ?? Number(part.quantity || 0) * Number(part.unit_price || 0)
      const partCode = part.part_number ? '<span style="font-family:monospace;color:#666">' + part.part_number + '</span>' : ''
      const meta = [partCode].filter(Boolean).join(' &nbsp; ')
      return '<div style="display:flex;justify-content:space-between;gap:16px;border-bottom:1px solid #eee;padding:8px 0">'
        + '<div><div style="font-size:13px;font-weight:700">' + part.quantity + 'x ' + (part.name || 'Workshop part') + '</div>'
        + (meta ? '<div style="font-size:12px;color:#777;margin-top:2px">' + meta + '</div>' : '')
        + '</div>'
        + (sellTotal > 0 ? '<div style="font-size:12px;color:#2d9e6b;font-weight:700">&pound;' + sellTotal.toFixed(2) + '</div>' : '')
        + '</div>'
    }).join('')
  }

  function reportMoney(value) {
    return '&pound;' + (Number(value || 0)).toFixed(2)
  }

  function invoiceAmountHtml(value) {
    const amount = Number(value || 0)
    return amount < 0 ? '-' + reportMoney(Math.abs(amount)) : reportMoney(amount)
  }

  function invoiceHtml(invoiceData = {}) {
    if (invoiceData.vat_document) return vatDocumentHtml(invoiceData)
    const lines = Array.isArray(invoiceData.lines) ? invoiceData.lines : []
    const rows = lines.length
      ? lines.map(line => {
        const quantity = Number(line.quantity || 1)
        const showUnit = quantity !== 1 && Number(line.unit_price || 0) > 0
        return '<tr>'
          + '<td style="padding:8px 10px;border-bottom:1px solid #eee">'
          + '<strong style="display:block;font-size:13px">' + escapeHtml(line.description || 'Workshop item') + '</strong>'
          + '<span style="display:block;font-size:11px;color:#777;text-transform:capitalize;margin-top:2px">' + escapeHtml(String(line.type || '').replace(/_/g, ' ')) + '</span>'
          + '</td>'
          + '<td style="padding:8px 10px;border-bottom:1px solid #eee;text-align:right;white-space:nowrap">' + escapeHtml(quantity.toFixed(quantity % 1 ? 2 : 0)) + '</td>'
          + '<td style="padding:8px 10px;border-bottom:1px solid #eee;text-align:right;white-space:nowrap">' + (showUnit ? reportMoney(line.unit_price) : '') + '</td>'
          + '<td style="padding:8px 10px;border-bottom:1px solid #eee;text-align:right;white-space:nowrap;font-weight:700">' + invoiceAmountHtml(line.line_total) + '</td>'
          + '</tr>'
      }).join('')
      : '<tr><td colspan="4" style="padding:8px 10px;color:#777">No billable invoice lines recorded.</td></tr>'

    return '<table style="width:100%;border-collapse:collapse;margin-top:8px">'
      + '<thead><tr>'
      + '<th style="text-align:left;font-size:11px;color:#777;text-transform:uppercase;padding:8px 10px;border-bottom:2px solid #ddd">Description</th>'
      + '<th style="text-align:right;font-size:11px;color:#777;text-transform:uppercase;padding:8px 10px;border-bottom:2px solid #ddd">Qty</th>'
      + '<th style="text-align:right;font-size:11px;color:#777;text-transform:uppercase;padding:8px 10px;border-bottom:2px solid #ddd">Unit</th>'
      + '<th style="text-align:right;font-size:11px;color:#777;text-transform:uppercase;padding:8px 10px;border-bottom:2px solid #ddd">Total</th>'
      + '</tr></thead><tbody>' + rows + '</tbody>'
      + '<tfoot>'
      + '<tr><td colspan="3" style="padding:9px 10px;background:#fafafa;font-weight:700">Subtotal</td><td style="padding:9px 10px;background:#fafafa;text-align:right;font-weight:700">' + reportMoney(invoiceData.subtotal) + '</td></tr>'
      + (Number(invoiceData.discounts || 0) > 0 ? '<tr><td colspan="3" style="padding:9px 10px;background:#fafafa;font-weight:700">Discounts</td><td style="padding:9px 10px;background:#fafafa;text-align:right;font-weight:700">-' + reportMoney(invoiceData.discounts) + '</td></tr>' : '')
      + (Number(invoiceData.adjustments || 0) > 0 ? '<tr><td colspan="3" style="padding:9px 10px;background:#fafafa;font-weight:700">Adjustments</td><td style="padding:9px 10px;background:#fafafa;text-align:right;font-weight:700">' + reportMoney(invoiceData.adjustments) + '</td></tr>' : '')
      + '<tr><td colspan="3" style="padding:12px 10px;background:#111;color:#fff;font-weight:800">Final total</td><td style="padding:12px 10px;background:#111;color:#fff;text-align:right;font-weight:800">' + reportMoney(invoiceData.total) + '</td></tr>'
      + '</tfoot></table>'
  }

  const hasSettings = job.b_air || job.a_air || job.b_lsc || job.a_lsc ||
    job.b_hsc || job.a_hsc || job.b_lsr || job.a_lsr || job.b_hsr || job.a_hsr || job.b_sag || job.a_sag
  const hasRec = job.interval || job.next_service || job.further_work || job.setup_rec

  const html = '<!DOCTYPE html><html><head><meta charset="UTF-8">'
    + '<title>Service Report — ' + (customer ? customer.first_name + ' ' + customer.last_name : '') + ' — ' + fmtDate(job.date) + '</title>'
    + '<style>'
    + 'body{font-family:"Helvetica Neue",Arial,sans-serif;margin:0;padding:34px;background:#f3f0eb;color:#1a1a1a;font-size:14px;line-height:1.6}'
    + '.report-shell{max-width:820px;margin:0 auto;background:#fff;border:1px solid #ded8cf;box-shadow:0 20px 50px rgba(22,18,14,.12)}'
    + '.hdr{display:flex;justify-content:space-between;align-items:flex-start;padding:34px 38px 26px;background:#111;color:#fff;border-bottom:4px solid #e85d04;margin-bottom:0}'
    + '.logo{font-size:24px;font-weight:800;letter-spacing:.06em;text-transform:uppercase}'
    + '.logo span{color:#e85d04}'
    + '.report-logo{display:block;max-width:220px;max-height:58px;object-fit:contain}'
    + '.meta{text-align:right;font-size:12px;color:#c9c9c9;line-height:1.8}'
    + '.report-body{padding:34px 38px 38px}'
    + '.summary-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin:0 0 26px}'
    + '.summary-card{border:1px solid #eee;background:#fafafa;border-radius:7px;padding:12px 14px;min-height:58px}'
    + '.summary-card span{display:block;font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:.08em;color:#888;margin-bottom:4px}'
    + '.summary-card strong{display:block;font-size:13px;color:#111;line-height:1.35}'
    + '.section-box{border:1px solid #eee;border-radius:7px;padding:14px 16px;margin-top:12px;background:#fff}'
    + 'h2{font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:.09em;color:#e85d04;margin:30px 0 10px;padding-bottom:7px;border-bottom:1px solid #eee}'
    + 'table{width:100%;border-collapse:collapse}'
    + '.th{font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;padding:0 8px 6px 0;color:#999;border-bottom:2px solid #eee}'
    + '.pass-box{background:#f0faf5;border:1px solid #2d9e6b;border-radius:6px;padding:16px 20px;margin-top:8px}'
    + '.pass-title{font-size:15px;font-weight:700;color:#2d9e6b;margin-bottom:10px;display:flex;align-items:center;gap:8px}'
    + '.fail-box{background:#fff5f5;border:1px solid #c0392b;border-radius:6px;padding:16px 20px;margin-top:8px}'
    + '.total-box{display:flex;justify-content:space-between;align-items:center;background:#111;color:#fff;border-left:4px solid #e85d04;padding:18px 20px;margin-top:30px;border-radius:7px}'
    + '.total-box div:last-child{color:#fff!important}'
    + '.footer{margin-top:40px;padding-top:16px;border-top:1px solid #eee;font-size:11px;color:#888;text-align:center}'
    + '@media print{body{padding:0;background:#fff}.report-shell{box-shadow:none;border:0}.summary-grid{grid-template-columns:repeat(2,1fr)}}'
    + '</style></head><body><div class="report-shell">'
    + '<div class="hdr">'
    + '<div>' + reportBrandHtml(workshopName, workshopLogoUrl)
    + '<div style="font-size:12px;color:#888;margin-top:4px">Suspension Service Report</div></div>'
    + '<div class="meta">'
    + '<div><strong>Date:</strong> ' + fmtDate(job.date) + '</div>'
    + '<div><strong>Job ref:</strong> ' + job.id.slice(0, 8).toUpperCase() + '</div>'
    + (job.technician ? '<div><strong>Technician:</strong> ' + job.technician + '</div>' : '')
    + (job.duration_mins ? '<div><strong>Duration:</strong> ' + fmtDuration(job.duration_mins) + (job.start_time && job.finish_time ? ' (' + job.start_time + ' – ' + job.finish_time + ')' : '') + '</div>' : '')
    + '</div></div><div class="report-body">'
    + '<div class="summary-grid">'
    + '<div class="summary-card"><span>Customer</span><strong>' + (customer ? customer.first_name + ' ' + customer.last_name : 'Customer') + '</strong></div>'
    + '<div class="summary-card"><span>Component</span><strong>' + (component ? (component.year ? component.year + ' ' : '') + component.model : 'No component linked') + '</strong></div>'
    + '<div class="summary-card"><span>Service</span><strong>' + (serviceTemplateName || (scopeItems[0] || 'Workshop service')) + '</strong></div>'
    + '<div class="summary-card"><span>QC</span><strong>' + (qcPassed ? 'Passed' : qcFailed ? 'Failed' : 'Incomplete') + '</strong></div>'
    + '</div>'
    + '<h2>Customer</h2><table><tbody>'
    + (customer ? '<tr><td style="padding:5px 0;color:#666;font-size:13px;width:180px">Name</td><td style="font-size:13px">' + customer.first_name + ' ' + customer.last_name + '</td></tr>' : '')
    + (customer?.email ? '<tr><td style="padding:5px 0;color:#666;font-size:13px">Email</td><td style="font-size:13px">' + customer.email + '</td></tr>' : '')
    + (customer?.phone ? '<tr><td style="padding:5px 0;color:#666;font-size:13px">Phone</td><td style="font-size:13px">' + customer.phone + '</td></tr>' : '')
    + '</tbody></table>'
    + '<h2>Component</h2><table><tbody>'
    + '<tr><td style="padding:5px 0;color:#666;font-size:13px;width:180px">Description</td><td style="font-size:13px">' + (component ? (component.year ? component.year + ' ' : '') + component.model : '—') + '</td></tr>'
    + (component?.type ? '<tr><td style="padding:5px 0;color:#666;font-size:13px">Type</td><td style="font-size:13px">' + component.type.charAt(0).toUpperCase() + component.type.slice(1) + '</td></tr>' : '')
    + (component?.serial ? '<tr><td style="padding:5px 0;color:#666;font-size:13px">Serial Number</td><td style="font-size:13px;font-family:monospace">' + component.serial + '</td></tr>' : '')
    + (component?.travel ? '<tr><td style="padding:5px 0;color:#666;font-size:13px">Travel</td><td style="font-size:13px">' + component.travel + '</td></tr>' : '')
    + (serviceTemplateName ? '<tr><td style="padding:5px 0;color:#666;font-size:13px">Service Template</td><td style="font-size:13px">' + serviceTemplateName + '</td></tr>' : '')
    + '</tbody></table>'
    + (scopeItems.length ? '<h2>Work Carried Out</h2><div style="display:grid;grid-template-columns:1fr 1fr;gap:2px">'
      + scopeItems.map(s => '<div style="display:flex;gap:8px;align-items:center;padding:4px 0;font-size:13px"><span style="color:#2d9e6b">✓</span>' + s + '</div>').join('') + '</div>' : '')
    + (structuredSettings.length
      ? '<h2>Setup - Before &amp; After</h2>' + measurementTable(structuredSettings)
      : hasSettings ? '<h2>Setup — Before &amp; After</h2><table><thead><tr>'
        + '<th class="th"></th><th class="th" style="color:#888">Before Service</th><th class="th" style="color:#2d9e6b">After Service</th>'
        + '</tr></thead><tbody>'
        + srow('Air Pressure / Spring', job.b_air, job.a_air)
        + srow('Sag %', job.b_sag, job.a_sag)
        + srow('Coil Preload', job.b_preload, job.a_preload)
        + srow('Tokens / Spacers', job.b_tokens, job.a_tokens)
        + srow('LSC (Low Speed Compression)', job.b_lsc, job.a_lsc)
        + srow('HSC (High Speed Compression)', job.b_hsc, job.a_hsc)
        + srow('Pedal / Lockout', job.b_pedal, job.a_pedal)
        + srow('LSR (Low Speed Rebound)', job.b_lsr, job.a_lsr)
        + srow('HSR (High Speed Rebound)', job.b_hsr, job.a_hsr)
        + '</tbody></table>' : '')
    + (structuredServiceLog.length ? '<h2>Service Measurements</h2>' + measurementTable(structuredServiceLog) : '')
    + (rawEvidenceItems.length ? '<h2>Service Evidence</h2>' + evidenceHtml(rawEvidenceItems) : '')
    + (partsUsed.length ? '<h2>Parts Fitted</h2>' + partsUsedHtml(partsUsed) : '')
    + '<h2>Quality Control</h2>'
    + (qcPassed
      ? '<div class="pass-box"><div class="pass-title"><span>✓</span> Service QC Passed</div>'
        + qrow('Correct torque settings confirmed', job.qc_torque)
        + qrow('Correct pressures / spring installed', job.qc_pressure)
        + qrow('Correct clickers reset', job.qc_clickers)
        + qrow('Full travel smooth', job.qc_travel)
        + qrow('No topout / knock', job.qc_topout)
        + qrow('No oil leaks', job.qc_oil_leak)
        + qrow('No air loss detected', job.qc_air_loss)
        + qrow('All adjusters functional', job.qc_adjusters)
        + qrow('Returned to customer baseline', job.qc_baseline)
        + (qcSignedMeta ? '<div style="font-size:12px;color:#666;margin-top:8px">Signed off: ' + qcSignedMeta + '</div>' : '')
        + '</div>'
      : '<div class="fail-box"><div style="font-size:15px;font-weight:700;color:#c0392b;margin-bottom:4px">' + (qcFailed ? 'QC Failed' : 'QC Incomplete') + '</div>'
        + '<div style="font-size:13px;color:#666;margin-top:4px">' + (qcFailed ? 'QC was signed off as failed and needs attention before handover.' : 'Not all QC checks completed for this job.') + '</div>'
        + (qcSignedMeta ? '<div style="font-size:12px;color:#666;margin-top:8px">Signed off: ' + qcSignedMeta + '</div>' : '')
        + '</div>')
    + (hasRec ? '<h2>Recommendations</h2><table><tbody>'
      + (job.interval ? '<tr><td style="padding:5px 0;color:#666;font-size:13px;width:180px">Service Interval</td><td style="font-size:13px">' + job.interval + '</td></tr>' : '')
      + (job.next_service ? '<tr><td style="padding:5px 0;color:#666;font-size:13px">Next Service Due</td><td style="font-size:13px;font-weight:600;color:#e85d04">' + job.next_service + '</td></tr>' : '')
      + (job.further_work ? '<tr><td style="padding:5px 0;color:#666;font-size:13px;vertical-align:top">Further Work Required</td><td style="font-size:13px">' + job.further_work + '</td></tr>' : '')
      + (job.setup_rec ? '<tr><td style="padding:5px 0;color:#666;font-size:13px;vertical-align:top">Setup Notes</td><td style="font-size:13px">' + job.setup_rec + '</td></tr>' : '')
      + '</tbody></table>' : '')
    + '<h2>' + escapeHtml(invoice.title || 'Itemised invoice') + '</h2>'
    + invoiceHtml(invoice)
    + '<div class="footer">' + (footerDetails || workshopName) + '<br>Thank you for choosing ' + workshopName + ' for your suspension service.</div></div></div>'
    + '<script>window.onload=function(){window.print();}<\/script>'
    + '</body></html>'

  const blob = new Blob([html], { type: 'text/html' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url; a.target = '_blank'; a.rel = 'noopener'
  document.body.appendChild(a); a.click(); document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(url), 2000)
}
